import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS } from '../../assetManifest';
import { CONSTANTS } from '../../data';
import { AudioManager } from '../../lib/AudioManager';

// --- TUTORIAL CARD COMPONENT ---
const TutorialCard = ({ card, isSmall = false }: { card: { number: number; color: string; imageAlias: string }; isSmall?: boolean }) => {
  const colorMap = CONSTANTS.TUTORIAL_COLORS.cardColors;
  const sizeClasses = isSmall ? 'w-8 h-12' : 'w-12 h-16';
  const numberSize = isSmall ? 'text-xs' : 'text-lg';
  const imageSize = isSmall ? 'w-5 h-5' : 'w-9 h-9';
  
  const imagePath = (ASSETS.faces as any)[card.imageAlias]?.path;

  return (
    <div className={`${sizeClasses} rounded-md shadow-lg flex flex-col justify-start text-white ${(colorMap as any)[card.color]} border-2 border-white/20`}>
      <div className="w-full flex justify-start p-0.5">
        <span className={`font-bold ${numberSize}`}>{card.number}</span>
      </div>
      <div className="flex-grow flex items-center justify-center">
        {imagePath && <img src={imagePath} alt={`Face for card ${card.number}`} className={`${imageSize} object-contain`} />}
      </div>
    </div>
  );
};

// --- TUTORIAL CARD STACK COMPONENT ---
const TutorialCardStack = ({ cards, label, isSmall = false }: { cards: { number: number; color: string; imageAlias: string }[]; label?: string; isSmall?: boolean }) => {
  return (
    <div className="flex flex-col items-center gap-1">
      {label && <span className="text-xs font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>{label}</span>}
      <div className="relative">
        {cards.map((card, index) => (
          <div
            key={index}
            className="absolute"
            style={{ 
              zIndex: index,
              transform: `translate(${index * (isSmall ? 1 : 2)}px, ${index * (isSmall ? 1 : 2)}px)`
            }}
          >
            <TutorialCard card={card} isSmall={isSmall} />
          </div>
        ))}
        {/* Spacer to prevent overlap */}
        <div className={`${isSmall ? 'w-8 h-12' : 'w-12 h-16'} opacity-0`} 
             style={{ marginLeft: `${(cards.length - 1) * (isSmall ? 1 : 2)}px`, marginTop: `${(cards.length - 1) * (isSmall ? 1 : 2)}px` }} />
      </div>
      {cards.length > 1 && (
        <div className="text-xs font-bold bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center -mt-2">
          {cards.length}
        </div>
      )}
    </div>
  );
};

// --- ENHANCED INSTRUCTIONS MODAL COMPONENT ---
const InstructionsModal = ({ onClose, audioManager }: { onClose: () => void; audioManager: AudioManager }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = CONSTANTS.INSTRUCTIONS.STEPS.length;

  const nextStep = () => {
    audioManager.playSound('buttonClick');
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const prevStep = () => {
    audioManager.playSound('buttonClick');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderCardExample = (step: any) => {
    const cardExample = step.cardExample;
    if (!cardExample) return null;

    return (
      <motion.div
        className="rounded-xl p-6 border-2 shadow-inner backdrop-blur-sm"
        style={{
          background: `linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))`,
          borderColor: 'var(--color-accent)'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-3 h-3 rounded-full" style={{ background: `linear-gradient(to right, var(--color-accent), var(--color-secondary))` }}></div>
          <span className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>Visual Example</span>
        </div>

        {/* Game Objective Example */}
        {cardExample.stacks && cardExample.totalPoints && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 justify-center">
              {cardExample.stacks.map((stack: any, index: number) => (
                <TutorialCardStack key={index} cards={stack} label={`Stack ${index + 1}`} />
              ))}
            </div>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold border" style={{
                background: 'var(--color-accent)',
                color: 'var(--color-button-text)',
                borderColor: 'var(--color-secondary)'
              }}>
                <span>Total Score:</span>
                <span className="text-xl">{cardExample.totalPoints}</span>
                <span>points</span>
              </div>
            </div>
          </div>
        )}

        {/* Drawing Cards Example */}
        {cardExample.before && cardExample.drawnCard && cardExample.after && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="flex flex-wrap gap-4 justify-center items-center">
                <div className="space-y-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>Before</span>
                  <div className="flex gap-2">
                    {cardExample.before.map((stack: any, index: number) => (
                      <TutorialCardStack key={index} cards={stack} isSmall />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>Draw</span>
                  <TutorialCard card={cardExample.drawnCard} isSmall />
                  <motion.div
                    className="text-2xl"
                    style={{ color: 'var(--color-accent)' }}
                    animate={{ x: [0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    →
                  </motion.div>
                </div>
                <div className="space-y-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>After</span>
                  <div className="flex gap-2">
                    {cardExample.after.map((stack: any, index: number) => (
                      <TutorialCardStack key={index} cards={stack} isSmall />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stealing Example */}
        {cardExample.yourStack && cardExample.stealableStacks && cardExample.afterSteal && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="space-y-2">
                <span className="text-xs font-medium" style={{ color: 'var(--color-secondary)' }}>Your Stack</span>
                <div className="flex justify-center">
                  {cardExample.yourStack.map((stack: any, index: number) => (
                    <TutorialCardStack key={index} cards={stack} isSmall />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium" style={{ color: 'var(--color-accent)' }}>Stealable Stacks</span>
                <div className="flex gap-2 justify-center">
                  {cardExample.stealableStacks.map((stack: any, index: number) => (
                    <div key={index} className="relative">
                      <TutorialCardStack cards={stack} isSmall />
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full animate-pulse" style={{ background: 'var(--color-accent)' }}></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-xs font-medium" style={{ color: 'var(--color-secondary)' }}>After Stealing</span>
                <div className="flex justify-center">
                  {cardExample.afterSteal.map((stack: any, index: number) => (
                    <TutorialCardStack key={index} cards={stack} isSmall />
                  ))}
                </div>
              </div>
            </div>
            <div className="text-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold border" style={{
                background: 'var(--color-accent)',
                color: 'var(--color-button-text)',
                borderColor: 'var(--color-secondary)'
              }}>
                ⚡ Steal Action!
              </span>
            </div>
          </div>
        )}

        {/* Busting Example */}
        {cardExample.result === 'BUST' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="flex flex-wrap gap-4 justify-center items-center">
                <div className="space-y-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>Before Draw</span>
                  <div className="flex gap-2">
                    {cardExample.before.map((stack: any, index: number) => (
                      <TutorialCardStack key={index} cards={stack} isSmall />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>Draw</span>
                  <TutorialCard card={cardExample.drawnCard} isSmall />
                </div>
                <div className="flex flex-col items-center gap-2">
                  <motion.div
                    className="text-4xl font-bold text-red-400"
                    animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    💥
                  </motion.div>
                  <span className="text-lg font-bold text-red-400 animate-pulse">BUST!</span>
                </div>
              </div>
            </div>
            <div className="text-center px-4 py-2 rounded-lg border bg-red-500/20 text-red-300 border-red-500/30">
              <span className="font-bold">All cards lost! Turn ends immediately.</span>
            </div>
          </div>
        )}

        {/* End Turn Example */}
        {cardExample.action === 'END_TURN' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 justify-center">
              {cardExample.stacks.map((stack: any, index: number) => (
                <TutorialCardStack key={index} cards={stack} label={`Stack ${index + 1}`} />
              ))}
            </div>
            <div className="text-center">
              <motion.button
                className="px-6 py-2 rounded-lg font-bold shadow-lg"
                style={{
                  background: 'var(--color-secondary)',
                  color: 'var(--color-text)'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                End Turn
              </motion.button>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold border" style={{
                background: 'var(--color-accent)',
                color: 'var(--color-button-text)',
                borderColor: 'var(--color-secondary)'
              }}>
                <span>Score:</span>
                <span className="text-xl">{cardExample.totalPoints}</span>
                <span>points</span>
              </div>
            </div>
          </div>
        )}

        {/* Final Scores Example */}
        {cardExample.finalScores && (
          <div className="space-y-3">
            {cardExample.finalScores.map((player: any, index: number) => (
              <div
                key={index}
                className={`flex justify-between items-center px-4 py-2 rounded-lg border-2 ${
                  player.winner 
                    ? 'border-2' 
                    : 'border'
                }`}
                style={{
                  background: player.winner 
                    ? `linear-gradient(to right, var(--color-accent), var(--color-secondary))` 
                    : 'var(--color-background-light)',
                  borderColor: player.winner ? 'var(--color-accent)' : 'var(--color-text)',
                  color: player.winner ? 'var(--color-button-text)' : 'var(--color-text)'
                }}
              >
                <span className="font-bold">
                  {player.player}
                  {player.winner && ' 👑'}
                </span>
                <span className="font-bold text-lg">
                  {player.score}
                </span>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      className="fixed inset-0 z-[5000] flex items-center justify-center backdrop-blur-sm"
      style={{
        background: `linear-gradient(135deg, var(--color-primary), var(--color-primary-light), var(--color-background))`
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full blur-xl" style={{ background: 'var(--color-background-light)', opacity: 0.05 }}></div>
        <div className="absolute top-1/3 right-20 w-48 h-48 rounded-full blur-2xl" style={{ background: 'var(--color-accent)', opacity: 0.10 }}></div>
        <div className="absolute bottom-20 left-1/4 w-40 h-40 rounded-full blur-xl" style={{ background: 'var(--color-secondary)', opacity: 0.10 }}></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 rounded-full blur-lg" style={{ background: 'var(--color-background-light)', opacity: 0.05 }}></div>
      </div>

      <motion.div
        className="rounded-3xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col backdrop-blur-sm border-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary-dark), var(--color-primary))`,
          color: 'var(--color-text)',
          borderColor: 'var(--color-accent)'
        }}
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Enhanced Header */}
        <div className="p-8 relative overflow-hidden" style={{
          background: `linear-gradient(to right, var(--color-primary), var(--color-primary-light))`
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
          <div className="relative z-10">
            {/* Card back as decorative element */}
            <motion.div
              initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 120, damping: 10 }}
              className="absolute top-4 right-4"
            >
              <img
                src={ASSETS.cards.cardBack.path}
                alt="Card Back"
                className="w-12 h-16 drop-shadow-lg rounded-lg border-2"
                style={{ borderColor: 'var(--color-accent)' }}
                draggable={false}
              />
            </motion.div>
            
            <motion.h2 
              className="text-4xl font-extrabold text-center mb-2 drop-shadow-lg tracking-wide"
              style={{ color: 'var(--color-accent)' }}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {CONSTANTS.INSTRUCTIONS.TITLE}
            </motion.h2>
            <motion.p 
              className="text-center text-lg font-medium"
              style={{ color: 'var(--color-text)', opacity: 0.9 }}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {CONSTANTS.INSTRUCTIONS.SUBTITLE}
            </motion.p>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-4 left-4 w-16 h-16 rounded-full" style={{ background: 'var(--color-background-light)', opacity: 0.1 }}></div>
          <div className="absolute bottom-4 right-20 w-12 h-12 rounded-full" style={{ background: 'var(--color-background-light)', opacity: 0.1 }}></div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="h-3 relative" style={{ background: 'var(--color-primary-dark)' }}>
          <motion.div
            className="h-full relative overflow-hidden"
            style={{
              background: `linear-gradient(to right, var(--color-accent), var(--color-secondary))`
            }}
            initial={{ width: "0%" }}
            animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-pulse"></div>
          </motion.div>
          <div className="absolute inset-0 flex justify-between items-center px-2">
            {Array.from({ length: totalSteps }, (_, index) => (
              <div
                key={index}
                className={`w-4 h-4 rounded-full border-2 transition-all ${
                  index <= currentStep 
                    ? 'shadow-lg' 
                    : ''
                }`}
                style={{
                  background: index <= currentStep 
                    ? `linear-gradient(to right, var(--color-accent), var(--color-secondary))` 
                    : 'var(--color-primary-dark)',
                  borderColor: 'var(--color-text)'
                }}
              />
            ))}
          </div>
        </div>

        {/* Enhanced Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
                <div className="flex items-start gap-6">
                  <motion.div 
                    className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-2xl shadow-lg"
                    style={{
                      background: `linear-gradient(to right, var(--color-accent), var(--color-secondary))`,
                      color: 'var(--color-button-text)'
                    }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    {currentStep + 1}
                  </motion.div>
                  <div className="flex-1">
                    <motion.h3 
                      className="text-3xl font-bold drop-shadow-lg mb-4"
                      style={{ color: 'var(--color-accent)' }}
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      {CONSTANTS.INSTRUCTIONS.STEPS[currentStep].title}
                    </motion.h3>
                    <div className="leading-relaxed space-y-4" style={{ color: 'var(--color-text)', opacity: 0.9 }}>
                      {CONSTANTS.INSTRUCTIONS.STEPS[currentStep].content.map((paragraph, index) => (
                        <motion.p 
                          key={index}
                          className="text-lg"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 + index * 0.1 }}
                        >
                          {paragraph}
                        </motion.p>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Enhanced Visual Example */}
                {renderCardExample(CONSTANTS.INSTRUCTIONS.STEPS[currentStep])}

                {/* Text Example */}
                {CONSTANTS.INSTRUCTIONS.STEPS[currentStep].example && (
                  <motion.div
                    className="rounded-xl p-6 border-l-4"
                    style={{
                      background: `linear-gradient(to right, var(--color-primary), var(--color-primary-dark))`,
                      borderColor: 'var(--color-accent)'
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full" style={{ background: 'var(--color-accent)' }}></div>
                      <span className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--color-accent)' }}>Text Example</span>
                    </div>
                    <p className="font-medium italic" style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                      {CONSTANTS.INSTRUCTIONS.STEPS[currentStep].example}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Enhanced Navigation */}
        <div className="px-8 py-6 border-t" style={{
          background: 'var(--color-primary-dark)',
          borderColor: 'var(--color-accent)'
        }}>
          <div className="flex justify-between items-center">
            <motion.button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`px-8 py-3 rounded-xl font-bold text-lg transition-all ${
                currentStep === 0
                  ? 'cursor-not-allowed opacity-50'
                  : 'shadow-lg hover:shadow-xl'
              }`}
              style={{
                background: currentStep === 0 
                  ? 'var(--color-primary)' 
                  : `linear-gradient(to right, var(--color-primary), var(--color-primary-light))`,
                color: 'var(--color-text)'
              }}
              whileHover={currentStep > 0 ? { scale: 1.05 } : {}}
              whileTap={currentStep > 0 ? { scale: 0.95 } : {}}
            >
              ← Previous
            </motion.button>

            <div className="flex gap-3">
              {Array.from({ length: totalSteps }, (_, index) => (
                <motion.div
                  key={index}
                  className="w-4 h-4 rounded-full transition-all"
                  style={{
                    background: index === currentStep 
                      ? `linear-gradient(to right, var(--color-accent), var(--color-secondary))` 
                      : 'var(--color-primary)'
                  }}
                  whileHover={{ scale: 1.2 }}
                  animate={index === currentStep ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.5 }}
                />
              ))}
            </div>

            <motion.button
              onClick={nextStep}
              className="px-8 py-3 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all border-2"
              style={{
                background: 'var(--color-button)',
                color: 'var(--color-button-text)',
                borderColor: 'var(--color-accent)'
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {currentStep === totalSteps - 1 ? '🎮 Start Playing!' : 'Next →'}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InstructionsModal;
